// pages/strategic-playbook/index.js
// ════════════════════════════════════════════════════
// STRATEGIC PLAYBOOK — Dashboard (v1.0)
// ════════════════════════════════════════════════════
// Domain #2 in Shift. "The Study" theme — oak-paneled
// library aesthetic. See docs/STRATEGIC_PLAYBOOK_BUILD.md
// for the full spec.
//
// 8 widgets across 5 rows. Hardcoded data for v1.0,
// moves to Supabase in v1.1.
// ════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import { DOSSIERS, getDossier } from '../../data/strategic-playbook/dossiers';
import {
  PULSE,
  TRACKED_SITUATIONS,
  HYPOTHESES,
  CONFIDENCE_MOVEMENT,
  TECTONIC_SHIFTS,
  FEATURED_DOSSIER_ID,
  THINKER_QUOTE,
  WORTH_NOTING,
  BLACK_SWAN_GAUGE,
} from '../../data/strategic-playbook/pulse';

export default function StrategicPlaybookDashboard() {
  const featured = getDossier(FEATURED_DOSSIER_ID);

  return (
    <div className="sp-root">
      <Head>
        <title>Strategic Intelligence · Shift</title>
      </Head>

      <div className="sp-page">
        <TopBar active="dashboard" weekOf={PULSE.weekOf} />

        {/* ROW 1 — Hero */}
        <Hero pulse={PULSE.hero} />

        {/* ROW 2 — Three primary widgets */}
        <div className="sp-grid-3">
          <TrackedSituationsCard situations={TRACKED_SITUATIONS} />
          <HypothesisScoreboardCard hypotheses={HYPOTHESES} />
          <ConfidenceMovementCard movements={CONFIDENCE_MOVEMENT} />
        </div>

        {/* ROW 3 — Featured dossier + Tectonic Shifts */}
        <div className="sp-grid-2">
          <FeaturedDossierCard dossier={featured} />
          <TectonicShiftsCard shifts={TECTONIC_SHIFTS} />
        </div>

        {/* ROW 4 — Quote + Black Swan + Worth Noting */}
        <div className="sp-grid-3">
          <ThinkerQuoteCard quote={THINKER_QUOTE} />
          <BlackSwanGaugeCard gauge={BLACK_SWAN_GAUGE} />
          <WorthNotingCard items={WORTH_NOTING} />
        </div>

        <FooterMark />
      </div>
    </div>
  );
}

// ─── Top bar ─────────────────────────────────────────────
function TopBar({ active, weekOf }) {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Week of {weekOf}</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook" className={active === 'dashboard' ? 'sp-active' : ''}>
          Dashboard
        </Link>
        <Link
          href="/strategic-playbook/library"
          className={active === 'library' ? 'sp-active' : ''}
        >
          Library
        </Link>
        <Link
          href="/strategic-playbook/tools"
          className={active === 'tools' ? 'sp-active' : ''}
        >
          Tools
        </Link>
        <Link
          href="/strategic-playbook/decisions"
          className={active === 'decisions' ? 'sp-active' : ''}
        >
          Decisions
        </Link>
        <Link
          href="/strategic-playbook/simulate"
          className={active === 'simulate' ? 'sp-active' : ''}
        >
          Simulate
        </Link>
      </nav>
    </div>
  );
}

// ─── Hero (Pulse headline) ───────────────────────────────
function Hero({ pulse }) {
  return (
    <div className="sp-hero">
      <div className="sp-hero-eyebrow">{pulse.eyebrow}</div>
      <h2>{pulse.headline}</h2>
      <p>{pulse.subline}</p>
      <div className="sp-hero-meta">
        {pulse.meta.map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Tracked Situations ──────────────────────────────────
function TrackedSituationsCard({ situations }) {
  return (
    <div className="sp-card">
      <h3 className="sp-card-title">
        <span>Tracked Situations</span>
        <span className="sp-card-title-counter">{situations.length} active</span>
      </h3>
      {situations.map((s) => (
        <div key={s.id} className="sp-situation">
          <div className={`sp-situation-stage sp-stage-${s.stage}`} />
          <div className="sp-situation-body">
            <div className="sp-situation-name">{s.name}</div>
            <div className="sp-situation-shift">{s.shift}</div>
            <div className="sp-situation-tags">
              <span className={`sp-tag sp-pillar-bg-${s.pillars[0]} sp-pillar-${s.pillars[0]}`}>
                P{s.pillars[0]}
              </span>
              {s.pillars[1] && (
                <span className={`sp-tag sp-pillar-bg-${s.pillars[1]} sp-pillar-${s.pillars[1]}`}>
                  P{s.pillars[1]}
                </span>
              )}
              <span className="sp-tag" style={{ color: 'rgba(232, 221, 196, 0.55)' }}>
                {s.stageLabel}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Hypothesis Scoreboard ───────────────────────────────
function HypothesisScoreboardCard({ hypotheses }) {
  return (
    <div className="sp-card">
      <h3 className="sp-card-title">
        <span>Hypothesis Scoreboard</span>
        <span className="sp-card-title-counter">{hypotheses.length} under test</span>
      </h3>
      {hypotheses.map((h) => (
        <div key={h.id} className="sp-hypothesis">
          <div className="sp-hypothesis-id">{h.id}</div>
          <div className="sp-hypothesis-text">{h.text}</div>
          <div className="sp-hypothesis-score">
            <div className="sp-hypothesis-bar">
              <div
                className="sp-hypothesis-bar-fill"
                style={{ width: `${h.progress}%` }}
              />
            </div>
            <span>{h.score}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Confidence Movement ─────────────────────────────────
function ConfidenceMovementCard({ movements }) {
  return (
    <div className="sp-card">
      <h3 className="sp-card-title">
        <span>Confidence Movement</span>
        <span className="sp-card-title-counter">This week</span>
      </h3>
      {movements.map((m, i) => {
        const pct = Math.min(Math.abs(m.delta) * 2, 100);
        return (
          <div key={i} className="sp-movement-bar">
            <div className="sp-movement-name">{m.name}</div>
            <div className="sp-movement-track">
              {m.direction === 'up' && (
                <div
                  className="sp-movement-fill-pos"
                  style={{ width: `${pct}%`, marginLeft: '50%' }}
                />
              )}
              {m.direction === 'down' && (
                <div
                  className="sp-movement-fill-neg"
                  style={{
                    width: `${pct}%`,
                    marginLeft: `${50 - pct}%`,
                    marginRight: '50%',
                  }}
                />
              )}
            </div>
            <div
              className={`sp-movement-delta ${
                m.direction === 'up' ? 'up' : m.direction === 'down' ? 'down' : ''
              }`}
            >
              {m.delta > 0 && '+'}
              {m.delta}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Featured Dossier ────────────────────────────────────
function FeaturedDossierCard({ dossier }) {
  if (!dossier) return null;
  return (
    <div className="sp-featured">
      <div className="sp-featured-eyebrow">Featured Dossier · This Week</div>
      <h3>{dossier.title}</h3>
      <p className="sp-featured-summary">{dossier.summary}</p>
      <div className="sp-featured-meta">
        {dossier.pillars.map((p) => (
          <span key={p} className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}>
            P{p} · {dossier.pillarNames[dossier.pillars.indexOf(p)]}
          </span>
        ))}
        <span>·</span>
        <span>{dossier.readingTimeMin} min read</span>
        <span>·</span>
        <span>Confidence {dossier.confidence}</span>
      </div>
      <Link href={`/strategic-playbook/${dossier.id}`} className="sp-featured-cta">
        Read the Dossier →
      </Link>
    </div>
  );
}

// ─── Tectonic Shifts ─────────────────────────────────────
const DASHBOARD_SHIFTS_LIMIT = 5;

function TectonicShiftsCard({ shifts }) {
  const featured = shifts.slice(0, DASHBOARD_SHIFTS_LIMIT);
  const remaining = shifts.length - featured.length;
  return (
    <div className="sp-card">
      <h3 className="sp-card-title">
        <span>Tectonic Shifts</span>
        <Link href="/strategic-playbook/tectonic-shifts" className="sp-card-title-link">
          View all {shifts.length} →
        </Link>
      </h3>
      {featured.map((s) => (
        <Link
          key={s.id}
          href={`/strategic-playbook/tectonic/${s.slug || s.id}`}
          className="sp-shift sp-shift-link"
        >
          <div className="sp-shift-name">{s.name}</div>
          <div className="sp-shift-stages">
            {s.stages.map((stage, i) => {
              let cls = 'sp-shift-stage';
              if (i < s.currentStageIndex) cls += ' passed';
              if (i === s.currentStageIndex) cls += ' active';
              return <div key={stage} className={cls} />;
            })}
          </div>
          <div className="sp-shift-meta">
            <span>{s.stageLabel}</span>
            <span>{s.note}</span>
          </div>
        </Link>
      ))}
      {remaining > 0 && (
        <Link href="/strategic-playbook/tectonic-shifts" className="sp-shift-more">
          + {remaining} more tracked →
        </Link>
      )}
    </div>
  );
}

// ─── Thinker Quote ───────────────────────────────────────
function ThinkerQuoteCard({ quote }) {
  return (
    <div className="sp-quote-card">
      <div className="sp-quote-eyebrow">Thinker of the Day</div>
      <blockquote className="sp-quote-body">{quote.body}</blockquote>
      <div className="sp-quote-attribution">
        — {quote.thinker}
        <div style={{ fontSize: 11, color: 'rgba(232, 221, 196, 0.40)', marginTop: 4 }}>
          {quote.context}
        </div>
      </div>
    </div>
  );
}

// ─── Black Swan Gauge ────────────────────────────────────
function BlackSwanGaugeCard({ gauge }) {
  return (
    <div className="sp-gauge-card">
      <h3 className="sp-card-title" style={{ justifyContent: 'center' }}>
        Black Swan Watch
      </h3>
      <div className="sp-gauge-status">{gauge.status}</div>
      <p className="sp-gauge-detail">{gauge.detail}</p>
      <div className="sp-gauge-active">{gauge.active}</div>
    </div>
  );
}

// ─── Worth Noting ────────────────────────────────────────
function WorthNotingCard({ items }) {
  return (
    <div className="sp-card">
      <h3 className="sp-card-title">
        <span>Worth Noting</span>
        <span className="sp-card-title-counter">Curated</span>
      </h3>
      {items.map((it, i) => (
        <div key={i} className="sp-noting">
          <div className="sp-noting-tag">{it.tag}</div>
          <p className="sp-noting-text">{it.text}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Footer ──────────────────────────────────────────────
function FooterMark() {
  return <div className="sp-footer-mark">— end of pulse · updated weekly mondays —</div>;
}
